import React from 'react';
import { useSearchParams, useNavigate, createSearchParams } from 'react-router';
import apiFunctions from '../../api';
import { useEffect, useState } from 'react';
import InfoCard from './components/InfoCard';
import { log, event, TabPanelProps } from './components/Interfaces';
import SearchBar from './components/SearchBar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import EventTab from './components/EventTab';
import LogTab from './components/LogTab';
import Tab from '@mui/material/Tab';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import Tabs from '@mui/material/Tabs';
import { CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { failure, unauthorized } from '../../types/api';
import { Errors } from '../../types/errors';

import './index.css';

const SessionInfo = () => {
  const [searchParams] = useSearchParams();
  const sessionUid = searchParams.get('id');
  const [value, setValue] = useState(0);
  const [searchVal, setSearchVal] = useState('');
  const [session, setSession] = useState(null);
  const [events, setEvent] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [logs, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animate, setAnimate] = useState(true);
  const [newOld, setNewOld] = useState(true);
  let navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    let subscribed = true;

    const getSessionData = async () => {
      if (subscribed) {
        await apiFunctions.apiPost('OnDemand/GetSessions', { SearchText: sessionUid }).then((res) => {
          if (res === failure) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.SessionInfoCallFailed })}`,
            });
          }
          if (res === unauthorized) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.Unauthorized })}`,
            });
          }

          setSession(res[0]);
        });

        await apiFunctions.apiPost('OnDemand/GetEventLogs', { SessionUid: sessionUid }).then((res) => {
          if (res === failure) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.SessionInfoCallFailed })}`,
            });
          }

          setEvent(res);
          setFilteredEvents(res);
        });

        await apiFunctions.apiPost('OnDemand/GetLeadStatusEntries', { SessionUid: sessionUid }).then((res) => {
          if (res === failure) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.SessionInfoCallFailed })}`,
            });
          }

          setLog(res);
          setFilteredLogs(res);
        });

        setLoading(false);
      }
    };

    if (loading && session == null) {
      getSessionData();
    }

    return () => {
      subscribed = false;
    };
  }, [loading, navigate, session, sessionUid]);

  if (loading) {
    return <CircularProgress />;
  }

  const filterValues = (searchVal: string) => {
    setSearchVal(searchVal);

    let forEvents = false;
    if (value === 0) {
      forEvents = true;
    }

    if (searchVal.trim() === '') {
      setFilteredEvents(events);
    } else {
      const regEx = new RegExp(searchVal.trim());

      if (forEvents && events.length > 0) {
        const newEvents = events.filter(
          (event: event) =>
            regEx.test(format(toZonedTime(event.dateUTC, '-10:00'), 'MM/dd/yyyy, hh:mmaaa')) ||
            regEx.test(event.description) ||
            regEx.test(event.type),
        );

        setFilteredEvents(newEvents);
      } else {
        if (logs.length > 0) {
          const newLogs = logs.filter(
            (log: log) =>
              regEx.test(format(toZonedTime(log.dateUTC, '-10:00'), 'MM/dd/yyyy, hh:mmaaa')) ||
              regEx.test(log.leadType) ||
              regEx.test(log.leadStatusType) ||
              regEx.test(log.leadStatusText),
          );

          setFilteredLogs(newLogs);
        }
      }
    }
  };

  const sortOldNew = () => {
    if (!newOld) {
      setFilteredEvents(filteredEvents.reverse());
      setFilteredLogs(filteredLogs.reverse());

      setNewOld(true);
    }
  };

  const sortNewOld = () => {
    if (newOld) {
      setFilteredEvents(filteredEvents.reverse());
      setFilteredLogs(filteredLogs.reverse());

      setNewOld(false);
    }
  };

  // Everything below is for using Tabs in mui
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    filterValues('');

    if (newValue === 0 && value === 1) {
      setAnimate(false);
    }
  };

  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        className="panel"
        hidden={value !== index}
        id={`full-width-tabpanel-${index}`}
        aria-labelledby={`full-width-tab-${index}`}
        {...other}>
        {value === index && (
          <Box sx={{ p: 3 }}>
            <Typography component={'span'}>{children}</Typography>
          </Box>
        )}
      </div>
    );
  }

  function a11yProps(index: number) {
    return {
      id: `full-width-tab-${index}`,
      'aria-controls': `full-width-tabpanel-${index}`,
    };
  }

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (animate) {
      setAnimate(false);
    }
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="container">
      <div className="backbutton" onClick={() => navigate(-1)}>
        <IconButton>
          <KeyboardBackspaceIcon fontSize="large" />
        </IconButton>
      </div>

      <div className="lookup">
        <div className="entry">
          <InfoCard info={session as any} />
        </div>

        <SearchBar searchVal={searchVal} filterValues={filterValues} />
      </div>

      <Box className="wrapper">
        <Box className="tabs" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} variant="fullWidth">
            <Tab label="Event Logs" {...a11yProps(0)} />
            <Tab label="Lead Status Entries" {...a11yProps(1)} />

            <IconButton
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}>
              <MoreVertIcon />
            </IconButton>

            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}>
              <MenuItem onClick={sortOldNew} sx={{ color: newOld ? 'blue' : 'black' }}>
                Sort Oldest to Newest
              </MenuItem>
              <MenuItem onClick={sortNewOld} sx={{ color: newOld ? 'black' : 'blue' }}>
                Sort Newest to Oldest
              </MenuItem>
            </Menu>
          </Tabs>
        </Box>

        <div className="cards">
          <TabPanel value={value} index={0}>
            <div className="events">
              <EventTab events={filteredEvents as any} animate={animate} />
            </div>
          </TabPanel>
          <TabPanel value={value} index={1}>
            <div className="events">
              <LogTab logs={filteredLogs as any} animate={animate} />
            </div>
          </TabPanel>
        </div>
      </Box>
    </div>
  );
};

export default SessionInfo;
